import { create } from 'zustand';
import {
  COPY_DATA_STEP_LABELS,
  type CopyDataJobSnapshot,
  type CopyDataPhase,
  type CopyDataSignalPayload,
  type CopyDataStepKey,
  type CopyDataStepState,
  type CopyDataStepStatus,
} from '@/types/copy-data-progress';
import {
  clearCopyDataJobSnapshot,
  logCopyDataTrace,
  readCopyDataJobSnapshot,
  writeCopyDataJobSnapshot,
} from '@/utils/copyDataProgressSync';
import {
  normalizeCopyDataStepKey,
  normalizeCopyDataStepStatus,
} from '@/services/tenant/tenant-api-config';

const STEP_STATUS_RANK: Record<CopyDataStepStatus, number> = {
  pending: 0,
  running: 1,
  done: 2,
  skipped: 2,
  failed: 2,
};

function maxStepStatus(current: CopyDataStepStatus, incoming: CopyDataStepStatus): CopyDataStepStatus {
  return STEP_STATUS_RANK[incoming] > STEP_STATUS_RANK[current] ? incoming : current;
}

function calcPercent(
  steps: CopyDataStepState[],
  override?: number,
  options?: { capWhileRunning?: boolean }
): number {
  const stepPercent = !steps.length
    ? 0
    : Math.round(
        (steps.filter((s) => s.status === 'done' || s.status === 'skipped').length / steps.length) *
          100
      );

  let percent = stepPercent;
  if (typeof override === 'number' && Number.isFinite(override)) {
    // BE percent + step percent — lấy cao hơn, tránh poll/SignalR gửi % lùi
    percent = Math.max(stepPercent, Math.min(100, Math.max(0, override)));
  }

  // API chính (activate/finalize) vẫn chạy sau khi các bước copy xong — chưa complete thì tối đa 99%.
  if (options?.capWhileRunning && percent >= 100) {
    return 99;
  }
  return percent;
}

/** % khi running chỉ được tăng, không nhảy lùi. */
function resolveRunningPercent(
  currentPercent: number,
  steps: CopyDataStepState[],
  override?: number
): number {
  return Math.max(currentPercent, calcPercent(steps, override, { capWhileRunning: true }));
}

function markPrecedingDone(steps: CopyDataStepState[], currentKey: CopyDataStepKey): CopyDataStepState[] {
  const idx = steps.findIndex((s) => s.stepKey === currentKey);
  if (idx < 0) return steps;
  return steps.map((step, i) => {
    if (i < idx && (step.status === 'pending' || step.status === 'running')) {
      return { ...step, status: 'done' as const };
    }
    return step;
  });
}

function applyStepUpdate(
  steps: CopyDataStepState[],
  stepKey?: CopyDataStepKey,
  stepStatus?: CopyDataStepStatus
): CopyDataStepState[] {
  if (!stepKey || !stepStatus) return steps;
  let next = markPrecedingDone(steps, stepKey);
  next = next.map((step) =>
    step.stepKey === stepKey ? { ...step, status: stepStatus } : step
  );
  return next;
}

function applyStepsSnapshot(
  steps: CopyDataStepState[],
  snapshot?: Array<{ stepKey: string; status: string }>
): CopyDataStepState[] {
  if (!snapshot?.length) return steps;
  return steps.map((step) => {
    const found = snapshot.find((s) => normalizeCopyDataStepKey(s.stepKey) === step.stepKey);
    if (!found) return step;
    const status = normalizeCopyDataStepStatus(found.status);
    if (!status) return step;
    return { ...step, status: maxStepStatus(step.status, status) };
  });
}

function allStepsDone(steps: CopyDataStepState[]): CopyDataStepState[] {
  return steps.map((s) =>
    s.status === 'skipped' ? s : { ...s, status: 'done' as const }
  );
}

function toSnapshot(state: {
  jobId: string | null;
  tuNam: number | null;
  denNam: number | null;
  phase: CopyDataPhase;
  steps: CopyDataStepState[];
  percent: number;
  errorMessage?: string;
  minimized: boolean;
}): CopyDataJobSnapshot | null {
  if (!state.jobId || state.tuNam == null || state.denNam == null) return null;
  if (state.phase === 'idle') return null;
  return {
    jobId: state.jobId,
    tuNam: state.tuNam,
    denNam: state.denNam,
    phase: state.phase,
    steps: state.steps,
    percent: state.percent,
    errorMessage: state.errorMessage,
    minimized: state.minimized,
    t: Date.now(),
  };
}

type CopyDataProgressState = {
  jobId: string | null;
  tuNam: number | null;
  denNam: number | null;
  phase: CopyDataPhase;
  steps: CopyDataStepState[];
  percent: number;
  errorMessage?: string;
  minimized: boolean;
  visible: boolean;
  startJob: (payload: {
    jobId: string;
    tuNam: number;
    denNam: number;
    steps: CopyDataStepState[];
  }) => void;
  setJobId: (jobId: string) => void;
  applySignal: (signal: CopyDataSignalPayload) => void;
  applyStatus: (signal: CopyDataSignalPayload) => void;
  completeJob: (message?: string) => void;
  failJob: (message?: string) => void;
  dismiss: () => void;
  setMinimized: (minimized: boolean) => void;
  hydrateFromStorage: () => void;
  applyRemoteSnapshot: (snapshot: CopyDataJobSnapshot | null) => void;
};

function stepsSignature(steps: CopyDataStepState[]): string {
  return steps.map((s) => `${s.stepKey}:${s.status}`).join('|');
}

function isSameProgressState(
  state: Pick<CopyDataProgressState, 'jobId' | 'phase' | 'percent' | 'steps' | 'errorMessage'>,
  next: {
    jobId?: string | null;
    phase: CopyDataPhase;
    percent: number;
    steps: CopyDataStepState[];
    errorMessage?: string;
  }
): boolean {
  return (
    (next.jobId ?? state.jobId) === state.jobId &&
    next.phase === state.phase &&
    next.percent === state.percent &&
    (next.errorMessage ?? undefined) === (state.errorMessage ?? undefined) &&
    stepsSignature(next.steps) === stepsSignature(state.steps)
  );
}

function persist(state: CopyDataProgressState): void {
  const snap = toSnapshot(state);
  if (snap) writeCopyDataJobSnapshot(snap);
  else clearCopyDataJobSnapshot();
}

export const useCopyDataProgressStore = create<CopyDataProgressState>((set, get) => ({
  jobId: null,
  tuNam: null,
  denNam: null,
  phase: 'idle',
  steps: [],
  percent: 0,
  errorMessage: undefined,
  minimized: false,
  visible: false,

  startJob: ({ jobId, tuNam, denNam, steps }) => {
    logCopyDataTrace('startJob', { jobId, tuNam, denNam, steps: steps.map((s) => s.stepKey) });
    set({
      jobId,
      tuNam,
      denNam,
      phase: 'running',
      steps,
      percent: calcPercent(steps),
      errorMessage: undefined,
      minimized: false,
      visible: true,
    });
    persist(get());
  },

  setJobId: (jobId) => {
    if (!jobId || get().jobId === jobId) return;
    set({ jobId });
    persist(get());
  },

  applySignal: (signal) => {
    const state = get();
    if (signal.jobId && state.jobId && signal.jobId !== state.jobId) {
      const isProvisional = state.jobId.startsWith('local-');
      // Ignore unrelated jobs; allow BE jobId to replace provisional client id
      if (state.phase === 'running' && !isProvisional) {
        logCopyDataTrace('applySignal-ignore-other-job', {
          signalJobId: signal.jobId,
          current: state.jobId,
        });
        return;
      }
    }

    logCopyDataTrace('applySignal', {
      phase: signal.phase,
      stepKey: signal.stepKey,
      stepStatus: signal.stepStatus,
      percent: signal.percent,
      jobId: signal.jobId,
    });

    if (signal.phase === 'start') {
      const hasProgress = state.steps.some(
        (s) => s.status === 'running' || s.status === 'done' || s.status === 'failed'
      );
      const steps =
        signal.steps && state.steps.length
          ? applyStepsSnapshot(state.steps, signal.steps)
          : state.steps.length && !hasProgress
            ? state.steps.map((s) => ({
                ...s,
                status: 'pending' as CopyDataStepStatus,
              }))
            : state.steps;
      const percent = resolveRunningPercent(state.percent, steps, signal.percent);
      const next = {
        jobId: signal.jobId || state.jobId,
        phase: 'running' as const,
        steps,
        percent,
        errorMessage: undefined as string | undefined,
      };
      if (isSameProgressState(state, next)) return;
      set({
        jobId: next.jobId,
        tuNam: signal.tuNam ?? state.tuNam,
        denNam: signal.denNam ?? state.denNam,
        phase: 'running',
        steps,
        percent,
        errorMessage: undefined,
        visible: true,
      });
      persist(get());
      return;
    }

    if (signal.phase === 'progress') {
      // Không cho poll/SignalR progress kéo job đã complete/failed về running
      if (state.phase === 'complete' || state.phase === 'failed') {
        return;
      }
      let steps = applyStepsSnapshot(state.steps, signal.steps);
      steps = applyStepUpdate(steps, signal.stepKey, signal.stepStatus);
      if (signal.stepKey && !signal.stepStatus) {
        steps = applyStepUpdate(steps, signal.stepKey, 'running');
      }
      const percent = resolveRunningPercent(state.percent, steps, signal.percent);
      const next = {
        jobId: signal.jobId || state.jobId,
        phase: 'running' as const,
        steps,
        percent,
      };
      if (isSameProgressState(state, next)) return;
      set({
        jobId: next.jobId,
        tuNam: signal.tuNam ?? state.tuNam,
        denNam: signal.denNam ?? state.denNam,
        phase: 'running',
        steps,
        percent,
        visible: true,
      });
      persist(get());
      return;
    }

    if (signal.phase === 'complete') {
      if (state.phase === 'complete' && state.percent === 100) return;
      const steps = allStepsDone(
        signal.steps ? applyStepsSnapshot(state.steps, signal.steps) : state.steps
      );
      set({
        phase: 'complete',
        steps,
        percent: 100,
        errorMessage: undefined,
        visible: true,
      });
      persist(get());
      return;
    }

    if (signal.phase === 'failed') {
      if (state.phase === 'complete') {
        logCopyDataTrace('applySignal-failed-ignored-already-complete', {
          jobId: signal.jobId,
        });
        return;
      }
      let steps = applyStepsSnapshot(state.steps, signal.steps);
      if (signal.stepKey) {
        steps = applyStepUpdate(steps, signal.stepKey, 'failed');
      } else {
        steps = steps.map((s) =>
          s.status === 'running' ? { ...s, status: 'failed' as const } : s
        );
      }
      set({
        phase: 'failed',
        steps,
        percent: calcPercent(steps, signal.percent),
        errorMessage: signal.message || state.errorMessage || 'Chuyển dữ liệu thất bại',
        visible: true,
        minimized: false,
      });
      persist(get());
    }
  },

  applyStatus: (signal) => {
    get().applySignal(signal);
  },

  completeJob: (message) => {
    const steps = allStepsDone(get().steps);
    set({
      phase: 'complete',
      steps,
      percent: 100,
      errorMessage: message,
      visible: true,
    });
    persist(get());
  },

  failJob: (message) => {
    const state = get();
    // HTTP abort/lỗi sau khi SignalR đã complete (thường khi rời /chuyen-du-lieu) — giữ thành công
    if (state.phase === 'complete') {
      logCopyDataTrace('failJob-ignored-already-complete', { message });
      return;
    }
    if (state.phase === 'idle') return;
    // Checklist đã xong hết mà vẫn bị fail từ HTTP → coi như complete (BE đã chạy xong các bước)
    const allDone =
      state.steps.length > 0 &&
      state.steps.every((s) => s.status === 'done' || s.status === 'skipped');
    if (allDone && state.phase === 'running') {
      logCopyDataTrace('failJob-promoted-to-complete', { message });
      get().completeJob();
      return;
    }
    const steps = state.steps.map((s) =>
      s.status === 'running' ? { ...s, status: 'failed' as const } : s
    );
    set({
      phase: 'failed',
      steps,
      percent: calcPercent(steps),
      errorMessage: message || 'Chuyển dữ liệu thất bại',
      visible: true,
      minimized: false,
    });
    persist(get());
  },

  dismiss: () => {
    set({
      jobId: null,
      tuNam: null,
      denNam: null,
      phase: 'idle',
      steps: [],
      percent: 0,
      errorMessage: undefined,
      minimized: false,
      visible: false,
    });
    clearCopyDataJobSnapshot();
  },

  setMinimized: (minimized) => {
    set({ minimized });
    persist(get());
  },

  hydrateFromStorage: () => {
    const snap = readCopyDataJobSnapshot();
    if (!snap) return;
    set({
      jobId: snap.jobId,
      tuNam: snap.tuNam,
      denNam: snap.denNam,
      phase: snap.phase,
      steps: snap.steps,
      percent: snap.percent,
      errorMessage: snap.errorMessage,
      minimized: snap.minimized,
      visible: snap.phase !== 'idle',
    });
  },

  applyRemoteSnapshot: (snapshot) => {
    if (!snapshot) {
      if (get().phase === 'running') return;
      get().dismiss();
      return;
    }
    const state = get();
    const sameRunningJob =
      snapshot.jobId === state.jobId &&
      state.phase === 'running' &&
      snapshot.phase === 'running' &&
      state.steps.length > 0 &&
      snapshot.steps.length > 0;

    const mergedSteps = sameRunningJob
      ? state.steps.map((step) => {
          const remote = snapshot.steps.find((s) => s.stepKey === step.stepKey);
          if (!remote) return step;
          return { ...step, status: maxStepStatus(step.status, remote.status) };
        })
      : snapshot.steps;

    const mergedPercent =
      sameRunningJob ? Math.max(state.percent, snapshot.percent) : snapshot.percent;

    if (
      isSameProgressState(state, {
        jobId: snapshot.jobId,
        phase: snapshot.phase,
        percent: mergedPercent,
        steps: mergedSteps,
        errorMessage: snapshot.errorMessage,
      }) &&
      state.minimized === snapshot.minimized
    ) {
      return;
    }
    set({
      jobId: snapshot.jobId,
      tuNam: snapshot.tuNam,
      denNam: snapshot.denNam,
      phase: snapshot.phase,
      steps: mergedSteps,
      percent: mergedPercent,
      errorMessage: snapshot.errorMessage,
      minimized: snapshot.minimized,
      visible: true,
    });
  },
}));

export function getCopyDataStepLabel(stepKey: CopyDataStepKey): string {
  return COPY_DATA_STEP_LABELS[stepKey];
}
